import { pgTable, text, boolean, timestamp, integer, index, uniqueIndex, foreignKey, jsonb, numeric, primaryKey } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { moduleItemType, homeworkStatus, homeworkType, videoProcessingStatus, moduleItemMediaType } from './enums';
import { teacher } from './content';
import { user } from './user';
import { trigger } from './trigger';
import { file } from './files';

/**
 * Course - Курс
 * Расширен для поддержки разных типов курсов и drip content
 */
export const course = pgTable('Course', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  slug: text().notNull(),
  courseType: text().default('ONLINE').notNull(),
  startDate: timestamp({ precision: 3, mode: 'date' }),
  endDate: timestamp({ precision: 3, mode: 'date' }),
  // Часы проведения курса в формате HH:MM (например '18:00'–'20:00').
  // Опциональны; применяются для всех типов кроме MEETING (Клуб).
  courseHoursFrom: text(),
  courseHoursTo: text(),
  schedule: jsonb(),
  isDripEnabled: boolean().default(false).notNull(),
  dripSchedule: jsonb(),
  accessDurationDays: integer(),
  onAccessExpiredTriggerId: integer(),
  onCourseCompletedTriggerId: integer(),
  durationMinutes: integer(),
  lessonsCount: integer().default(0).notNull(),
  thumbnailUrl: text(),
  thumbnailId: integer(),
  bannerImageId: integer(),
  pastImageId: integer(),
  previewVideoUrl: text(),
  order: integer().default(0).notNull(),
  isPublished: boolean().default(false).notNull(),
  price: numeric({ precision: 12, scale: 2 }),
  currency: text().default('UZS'),
  isPurchasable: boolean().default(false).notNull(),
  prices: jsonb(),
  baseCurrency: text().default('UZS'),
  deletedAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
  instructorId: integer(),
  scheduleTimezone: text().default('Asia/Tashkent'),
  scheduleNotificationSettings: jsonb(),
  capacity: integer(),
  bookedSeats: integer().default(0),
  // Аналитика курса: денормализованные счётчики просмотров и завершённости.
  // viewsCount — общее число просмотров (включая повторные).
  // uniqueViewersCount — количество уникальных учеников, открывших курс.
  // completionRate — % учеников с completedAt != null от общего числа
  // купивших, диапазон 0-100.
  viewsCount: integer().default(0).notNull(),
  uniqueViewersCount: integer().default(0).notNull(),
  completionRate: numeric({ precision: 5, scale: 2 }).default('0').notNull(),
  // Языковая аудитория курса. Контракт:
  // [] — курс виден всем пользователям;
  // ['ru'] — только пользователям с ru;
  // ['ru','uz'] — только ru или uz.
  // Используется для таргетинга MEETING-курсов (Клуб) и других курсов по языку.
  targetLanguages: jsonb().$type<string[]>().notNull().default(sql`'[]'::jsonb`),
}, (table) => [
  index('Course_isPublished_idx').using('btree', table.isPublished.asc().nullsLast()),
  index('Course_order_idx').using('btree', table.order.asc().nullsLast()),
  index('Course_slug_idx').using('btree', table.slug.asc().nullsLast()),
  index('Course_courseType_idx').using('btree', table.courseType.asc().nullsLast()),
  index('Course_startDate_idx').using('btree', table.startDate.asc().nullsLast()),
  index('Course_isPurchasable_idx').using('btree', table.isPurchasable.asc().nullsLast()),
  uniqueIndex('Course_slug_key').using('btree', table.slug.asc().nullsLast()),
  index('Course_deletedAt_idx').using('btree', table.deletedAt.desc().nullsLast()),
  index('Course_targetLanguages_idx').using('gin', table.targetLanguages),
  foreignKey({
    columns: [table.instructorId],
    foreignColumns: [teacher.id],
    name: 'Course_instructorId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.thumbnailId],
    foreignColumns: [file.id],
    name: 'Course_thumbnailId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.bannerImageId],
    foreignColumns: [file.id],
    name: 'Course_bannerImageId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.pastImageId],
    foreignColumns: [file.id],
    name: 'Course_pastImageId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.onAccessExpiredTriggerId],
    foreignColumns: [trigger.id],
    name: 'Course_onAccessExpiredTriggerId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.onCourseCompletedTriggerId],
    foreignColumns: [trigger.id],
    name: 'Course_onCourseCompletedTriggerId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

export const module = pgTable('Module', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  courseId: integer().notNull(),
  order: integer().default(0).notNull(),
  availableFrom: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('Module_courseId_idx').using('btree', table.courseId.asc().nullsLast()),
  index('Module_order_idx').using('btree', table.order.asc().nullsLast()),
  index('Module_availableFrom_idx').using('btree', table.availableFrom.asc().nullsLast()),
  foreignKey({
    columns: [table.courseId],
    foreignColumns: [course.id],
    name: 'Module_courseId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

/**
 * ModuleItem - Элемент модуля курса
 * Расширен для защиты видео через HLS
 */
export const moduleItem = pgTable('ModuleItem', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  moduleId: integer().notNull(),
  type: moduleItemType().default('VIDEO').notNull(),
  order: integer().default(0).notNull(),
  isPreview: boolean().default(false).notNull(),

  // Оригинальное видео
  videoUrl: text(),
  videoFileId: integer(),
  videoDuration: integer(),

  // Защита видео (HLS)
  isProtected: boolean().default(false).notNull(),
  hlsPlaylistUrl: text(), // m3u8 URL после конвертации
  hlsFileId: integer(),
  // Per-lesson постер для VIDEO (отделён от course.thumbnailUrl, чтобы карточки
  // уроков в секции «Continue Watching» и плеере показывали свой превью-кадр).
  posterUrl: text(),
  posterFileId: integer(),
  isManualPoster: boolean().default(false).notNull(),
  originalVideoUrl: text(), // Оригинальный URL (для перезаливки)
  processingStatus: videoProcessingStatus(), // PENDING, PROCESSING, READY, ERROR
  processingError: text(),

  // Привязка к Книге (для type=BOOK). FK объявлен только в SQL миграции
  // (0045_library_books.sql) — на уровне TS не объявляем, чтобы избежать
  // циклической зависимости course ↔ book ↔ pricing-tier.
  bookId: integer(),

  availableFrom: timestamp({ precision: 3, mode: 'date' }),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('ModuleItem_moduleId_idx').using('btree', table.moduleId.asc().nullsLast()),
  index('ModuleItem_order_idx').using('btree', table.order.asc().nullsLast()),
  index('ModuleItem_isProtected_idx').using('btree', table.isProtected.asc().nullsLast()),
  index('ModuleItem_processingStatus_idx').using('btree', table.processingStatus.asc().nullsLast()),
  index('ModuleItem_bookId_idx').using('btree', table.bookId.asc().nullsLast()),
  index('ModuleItem_posterFileId_idx').using('btree', table.posterFileId.asc().nullsLast()),
  index('ModuleItem_availableFrom_idx').using('btree', table.availableFrom.asc().nullsLast()),
  foreignKey({
    columns: [table.moduleId],
    foreignColumns: [module.id],
    name: 'ModuleItem_moduleId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.videoFileId],
    foreignColumns: [file.id],
    name: 'ModuleItem_videoFileId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.hlsFileId],
    foreignColumns: [file.id],
    name: 'ModuleItem_hlsFileId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.posterFileId],
    foreignColumns: [file.id],
    name: 'ModuleItem_posterFileId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

export const courseProgress = pgTable('CourseProgress', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userId: integer().notNull(),
  courseId: integer().notNull(),
  completedItems: integer().array(),
  lastWatchedItem: integer(),
  lastPosition: integer(),
  completedAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('CourseProgress_courseId_idx').using('btree', table.courseId.asc().nullsLast()),
  uniqueIndex('CourseProgress_userId_courseId_key').using('btree', table.userId.asc().nullsLast(), table.courseId.asc().nullsLast()),
  index('CourseProgress_userId_idx').using('btree', table.userId.asc().nullsLast()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'CourseProgress_userId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.courseId],
    foreignColumns: [course.id],
    name: 'CourseProgress_courseId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

/**
 * LessonProgress — прогресс просмотра на уровне отдельного урока (ModuleItem).
 * В отличие от CourseProgress.lastWatchedItem/lastPosition (один курс → одна
 * «последняя позиция»), хранит позицию по каждому уроку отдельно. Нужен для
 * секции «Continue Watching», где у одного курса может быть несколько начатых
 * уроков, а также для отображения прогресс-бара на карточке каждого урока.
 *
 * position — секунды для VIDEO; для ARTICLE пока 0 (можно будет использовать
 * под прогресс скролла в будущем).
 * duration — копия moduleItem.videoDuration на момент последнего обновления
 * (чтобы считать % прогресса без join'а); null для ARTICLE.
 */
export const lessonProgress = pgTable('LessonProgress', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userId: integer().notNull(),
  moduleItemId: integer().notNull(),
  position: integer().default(0).notNull(),
  duration: integer(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  uniqueIndex('LessonProgress_userId_moduleItemId_key').using('btree', table.userId.asc().nullsLast(), table.moduleItemId.asc().nullsLast()),
  index('LessonProgress_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('LessonProgress_moduleItemId_idx').using('btree', table.moduleItemId.asc().nullsLast()),
  index('LessonProgress_updatedAt_idx').using('btree', table.updatedAt.desc().nullsLast()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'LessonProgress_userId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.moduleItemId],
    foreignColumns: [moduleItem.id],
    name: 'LessonProgress_moduleItemId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

export const homework = pgTable('Homework', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userId: integer().notNull(),
  itemId: integer().notNull(),
  status: homeworkStatus().default('NOT_STARTED').notNull(),
  files: jsonb(),
  checklistItems: jsonb('checklist_items').$type<Array<{ text: string; isCompleted: boolean }>>(),
  notes: text(),
  noteItems: jsonb('note_items').$type<Array<{ id: string; text: string; createdAt: string }>>(),
  grade: integer(),
  feedback: text(),
  startedAt: timestamp({ precision: 3, mode: 'date' }),
  submittedAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('Homework_status_idx').using('btree', table.status.asc().nullsLast()),
  index('Homework_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('Homework_itemId_idx').using('btree', table.itemId.asc().nullsLast()),
  uniqueIndex('Homework_userId_itemId_key').using('btree', table.userId.asc().nullsLast(), table.itemId.asc().nullsLast()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'Homework_userId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.itemId],
    foreignColumns: [moduleItem.id],
    name: 'Homework_itemId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

/**
 * HomeworkConfig - Конфигурация домашнего задания
 * Определяет тип ДЗ, дедлайны и параметры стриков
 */
export const homeworkConfig = pgTable('HomeworkConfig', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  moduleItemId: integer().notNull(),
  homeworkType: homeworkType().default('ONE_TIME').notNull(),
  deadline: timestamp({ precision: 3, mode: 'date' }),
  streakDays: integer(),
  periodHours: integer(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  uniqueIndex('HomeworkConfig_moduleItemId_key').using('btree', table.moduleItemId.asc().nullsLast()),
  foreignKey({
    columns: [table.moduleItemId],
    foreignColumns: [moduleItem.id],
    name: 'HomeworkConfig_moduleItemId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

/**
 * CourseInstructor - Связь курса с преподавателями (many-to-many)
 */
export const courseInstructor = pgTable('CourseInstructor', {
  courseId: integer().notNull(),
  teacherId: integer().notNull(),
  order: integer().default(0).notNull(),
}, (table) => [
  index('CourseInstructor_courseId_idx').using('btree', table.courseId.asc().nullsLast()),
  index('CourseInstructor_teacherId_idx').using('btree', table.teacherId.asc().nullsLast()),
  foreignKey({
    columns: [table.courseId],
    foreignColumns: [course.id],
    name: 'CourseInstructor_courseId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.teacherId],
    foreignColumns: [teacher.id],
    name: 'CourseInstructor_teacherId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  primaryKey({ columns: [table.courseId, table.teacherId], name: 'CourseInstructor_pkey' }),
]);

export const courseWishlist = pgTable('CourseWishlist', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userId: integer().notNull(),
  courseId: integer().notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  uniqueIndex('CourseWishlist_userId_courseId_key').using('btree', table.userId.asc().nullsLast(), table.courseId.asc().nullsLast()),
  index('CourseWishlist_userId_idx').using('btree', table.userId.asc().nullsLast()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'CourseWishlist_userId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.courseId],
    foreignColumns: [course.id],
    name: 'CourseWishlist_courseId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

/**
 * ModuleItemMedia - Медиа для элемента модуля
 * Может содержать переводы, аудио, субтитры для видео
 */
export const moduleItemMedia = pgTable('ModuleItemMedia', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  moduleItemId: integer().notNull(),
  mediaType: moduleItemMediaType().notNull(),      // SUBTITLE, AUDIO, VIDEO
  languageCode: text().notNull(),
  mediaUrl: text().notNull(),
  fileId: integer(),
  duration: integer(),                             // длительность в секундах
  order: integer().default(0).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('ModuleItemMedia_moduleItemId_idx').using('btree', table.moduleItemId.asc().nullsLast()),
  uniqueIndex('ModuleItemMedia_item_lang_type_idx').using('btree', table.moduleItemId.asc().nullsLast(), table.languageCode.asc().nullsLast(), table.mediaType.asc().nullsLast()),
  foreignKey({
    columns: [table.moduleItemId],
    foreignColumns: [moduleItem.id],
    name: 'ModuleItemMedia_moduleItemId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.fileId],
    foreignColumns: [file.id],
    name: 'ModuleItemMedia_fileId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

/**
 * ModuleItemAttachment - Прикреплённые к уроку файлы (PDF, DOC, изображения и т.п.)
 * Используется для выдачи ученикам дополнительных материалов к уроку.
 * Порядок файлов определяется полем order (drag&drop в админке).
 */
export const moduleItemAttachment = pgTable('ModuleItemAttachment', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  moduleItemId: integer().notNull(),
  fileId: integer().notNull(),
  order: integer().default(0).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('ModuleItemAttachment_moduleItemId_order_idx').using('btree', table.moduleItemId.asc().nullsLast(), table.order.asc().nullsLast()),
  uniqueIndex('ModuleItemAttachment_moduleItemId_fileId_key').using('btree', table.moduleItemId.asc().nullsLast(), table.fileId.asc().nullsLast()),
  index('ModuleItemAttachment_fileId_idx').using('btree', table.fileId.asc().nullsLast()),
  foreignKey({
    columns: [table.moduleItemId],
    foreignColumns: [moduleItem.id],
    name: 'ModuleItemAttachment_moduleItemId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.fileId],
    foreignColumns: [file.id],
    name: 'ModuleItemAttachment_fileId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

export const homeworkCompletion = pgTable('HomeworkCompletion', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  homeworkId: integer().notNull(),
  userId: integer().notNull(),
  dayNumber: integer().notNull(),
  completedAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('HomeworkCompletion_homeworkId_idx').using('btree', table.homeworkId.asc().nullsLast()),
  index('HomeworkCompletion_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('HomeworkCompletion_completedAt_idx').using('btree', table.completedAt.asc().nullsLast()),
  uniqueIndex('HomeworkCompletion_homeworkId_dayNumber_key').using('btree', table.homeworkId.asc().nullsLast(), table.dayNumber.asc().nullsLast()),
  foreignKey({
    columns: [table.homeworkId],
    foreignColumns: [homework.id],
    name: 'HomeworkCompletion_homeworkId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'HomeworkCompletion_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);
